import { useCallback, useEffect, useMemo, useState } from "react";
import { CurrencyIcon } from "../../../Components/Atoms/CurrencyIcon";
import { useToast } from "../../../Hooks/toast";
import { useProgressAlert, mockProgressApi } from "../../../Hooks/useProgressAlert";
import { CurrencySelect } from "../ContaInternacional/components/CurrencySelect";
import {
  formatCrossborderAmount,
  formatTransactionRoute,
  getBankAccount,
  getBankAccountsForCustomer,
  getBeneficiaryRails,
  getCustomer,
  getCurrency,
  getModalPayoutQuote,
  getValutSendRails,
  MOCK_CUSTOMERS,
  PAYOUT_AMOUNT_LIMITS,
  PAYOUT_DESTINATION_CURRENCIES,
  PAYOUT_ORIGIN_CURRENCIES,
} from "./constants";
import { CrossborderSelect } from "./components/CrossborderSelect";
import { CrossborderAmountSection } from "./components/CrossborderAmountSection";
import { CrossborderFormSection } from "./components/CrossborderFormSection";
import { PaymentRailIcon } from "./components/PaymentRailIcon";
import {
  buildModalPayoutSummaryRows,
  CrossborderSummary,
} from "./components/CrossborderSummary";

const PAYOUT_FORM_ID = "crossborder-payout-form";

const formatRail = (rail) => (
  <>
    <PaymentRailIcon railId={rail.id} className="h-4 w-4" />
    <span className="min-w-0 truncate font-semibold text-brand-dark">{rail.label}</span>
  </>
);

const formatCustomer = (customer) => (
  <span className="min-w-0 truncate font-semibold text-brand-dark">{customer.label}</span>
);

const formatBankAccount = (account) => (
  <>
    <CurrencyIcon asset={account.currencyId} />
    <span className="min-w-0 truncate font-semibold text-brand-dark">{account.label}</span>
  </>
);

const originCurrencyOptions = PAYOUT_ORIGIN_CURRENCIES.map((id) => getCurrency(id)).filter(Boolean);
const destinationCurrencyOptions = PAYOUT_DESTINATION_CURRENCIES.map((id) => getCurrency(id)).filter(Boolean);

export const CrossborderPayoutContent = ({
  isActive = true,
  formId = PAYOUT_FORM_ID,
  showPageActions = false,
  onRequestQuote,
  onClose,
}) => {
  const toast = useToast();
  const progress = useProgressAlert();
  const [originCurrencyId, setOriginCurrencyId] = useState("usd");
  const [sendRailId, setSendRailId] = useState("wire");
  const [destinationCurrencyId, setDestinationCurrencyId] = useState("usd");
  const [beneficiaryRailId, setBeneficiaryRailId] = useState("wire");
  const [customerId, setCustomerId] = useState(MOCK_CUSTOMERS[0].id);
  const [bankAccountId, setBankAccountId] = useState("");
  const [amount, setAmount] = useState("");

  const sendRails = useMemo(() => getValutSendRails(originCurrencyId), [originCurrencyId]);

  const bankAccounts = useMemo(
    () => getBankAccountsForCustomer(customerId),
    [customerId]
  );

  const selectedBankAccount = useMemo(
    () => bankAccounts.find((account) => account.id === bankAccountId),
    [bankAccounts, bankAccountId]
  );

  const beneficiaryRails = useMemo(() => {
    if (selectedBankAccount?.countryId) {
      return getBeneficiaryRails(selectedBankAccount.countryId);
    }
    return getValutSendRails(destinationCurrencyId);
  }, [selectedBankAccount, destinationCurrencyId]);

  useEffect(() => {
    if (!sendRails.some((rail) => rail.id === sendRailId)) {
      setSendRailId(sendRails[0]?.id ?? "wire");
    }
  }, [sendRails, sendRailId]);

  useEffect(() => {
    if (!beneficiaryRails.some((rail) => rail.id === beneficiaryRailId)) {
      setBeneficiaryRailId(
        selectedBankAccount?.defaultRailId ?? beneficiaryRails[0]?.id ?? "wire"
      );
    }
  }, [beneficiaryRails, beneficiaryRailId, selectedBankAccount]);

  useEffect(() => {
    if (!bankAccounts.some((account) => account.id === bankAccountId)) {
      const first = bankAccounts[0];
      setBankAccountId(first?.id ?? "");
      if (first?.currencyId) {
        setDestinationCurrencyId(first.currencyId);
      }
    }
  }, [bankAccounts, bankAccountId]);

  useEffect(() => {
    if (!isActive) {
      setOriginCurrencyId("usd");
      setSendRailId("wire");
      setDestinationCurrencyId("usd");
      setBeneficiaryRailId("wire");
      setCustomerId(MOCK_CUSTOMERS[0].id);
      setBankAccountId(
        MOCK_CUSTOMERS[0] ? getBankAccountsForCustomer(MOCK_CUSTOMERS[0].id)[0]?.id ?? "" : ""
      );
      setAmount("");
    }
  }, [isActive]);

  const numericAmount = parseFloat(amount.replace(",", ".")) || 0;
  const limits = PAYOUT_AMOUNT_LIMITS[originCurrencyId] ?? PAYOUT_AMOUNT_LIMITS.usd;

  const quote = useMemo(
    () =>
      getModalPayoutQuote({
        originCurrencyId,
        destinationCurrencyId,
        sendRailId,
        beneficiaryRailId,
        amount: numericAmount,
      }),
    [originCurrencyId, destinationCurrencyId, sendRailId, beneficiaryRailId, numericAmount]
  );

  const { rows, footerRows } = buildModalPayoutSummaryRows(quote);

  const routePreview = formatTransactionRoute({
    originRailId: sendRailId,
    destinationRailId: beneficiaryRailId,
    fromCurrency: originCurrencyId,
    toCurrency: destinationCurrencyId,
  });

  const handleBankAccountChange = useCallback((accountId) => {
    setBankAccountId(accountId);
    const account = getBankAccount(accountId);
    if (account?.currencyId) {
      setDestinationCurrencyId(account.currencyId);
      if (account.defaultRailId) {
        setBeneficiaryRailId(account.defaultRailId);
      }
    }
  }, []);

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();

      if (
        !getCurrency(originCurrencyId) ||
        !getCurrency(destinationCurrencyId) ||
        !getCustomer(customerId) ||
        !getBankAccount(bankAccountId)
      ) {
        toast.addToast({
          type: "error",
          title: "Campos obrigatórios",
          description: "Preencha todos os campos obrigatórios.",
        });
        return;
      }

      if (!numericAmount || numericAmount <= 0) {
        toast.addToast({
          type: "error",
          title: "Valor inválido",
          description: "Informe um valor maior que zero.",
        });
        return;
      }

      if (numericAmount < limits.min || numericAmount > limits.max) {
        toast.addToast({
          type: "error",
          title: "Valor fora dos limites",
          description: `O valor deve estar entre ${formatCrossborderAmount(limits.min, originCurrencyId)} e ${formatCrossborderAmount(limits.max, originCurrencyId)}.`,
        });
        return;
      }

      progress
        .runWithProgress(
          () => mockProgressApi(),
          {
            loading: "Solicitando cotação...",
            loadingDescription: routePreview,
            success: "Cotação solicitada",
            successDescription: `Payout ${routePreview} para ${getBankAccount(bankAccountId)?.label}. Integração com API em desenvolvimento.`,
            error: "Falha ao solicitar cotação",
            errorDescription: "Não foi possível obter a cotação. Tente novamente.",
          },
          { beforeProgress: onClose }
        )
        .then(() => {
          onRequestQuote?.(quote);
        })
        .catch(() => {});
    },
    [
      bankAccountId,
      customerId,
      destinationCurrencyId,
      limits.max,
      limits.min,
      numericAmount,
      onClose,
      onRequestQuote,
      originCurrencyId,
      progress,
      quote,
      routePreview,
      toast,
    ]
  );

  return (
    <form id={formId} onSubmit={handleSubmit} className="space-y-4">
      <CrossborderFormSection title="Conta Valut (origem)">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <CurrencySelect
            id="payout-origin-currency"
            label="Moeda de origem (Valut)"
            value={originCurrencyId}
            onChange={setOriginCurrencyId}
            options={originCurrencyOptions}
          />
          <CrossborderSelect
            id="payout-send-rail"
            label="Forma de envio da Valut"
            required
            value={sendRailId}
            onChange={setSendRailId}
            options={sendRails}
            placeholder="Selecione a forma de envio"
            formatOption={formatRail}
            getSelected={(id) => sendRails.find((rail) => rail.id === id)}
          />
        </div>
      </CrossborderFormSection>

      <CrossborderFormSection title="Beneficiário">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <CurrencySelect
            id="payout-destination-currency"
            label="Moeda de destino"
            value={destinationCurrencyId}
            onChange={setDestinationCurrencyId}
            options={destinationCurrencyOptions}
          />
          <CrossborderSelect
            id="payout-beneficiary-rail"
            label="Forma de recebimento no destino"
            required
            value={beneficiaryRailId}
            onChange={setBeneficiaryRailId}
            options={beneficiaryRails}
            placeholder="Selecione a forma de recebimento"
            formatOption={formatRail}
            getSelected={(id) => beneficiaryRails.find((rail) => rail.id === id)}
          />
          <CrossborderSelect
            id="payout-customer"
            label="Cliente"
            required
            value={customerId}
            onChange={setCustomerId}
            options={MOCK_CUSTOMERS}
            placeholder="Selecione o cliente"
            formatOption={formatCustomer}
            getSelected={getCustomer}
            searchPlaceholder="Buscar cliente..."
          />
          <CrossborderSelect
            id="payout-bank-account"
            label="Conta bancária do beneficiário"
            required
            value={bankAccountId}
            onChange={handleBankAccountChange}
            options={bankAccounts}
            placeholder="Selecione a conta bancária"
            formatOption={formatBankAccount}
            getSelected={getBankAccount}
            emptyMessage="Nenhuma conta disponível para este cliente."
          />
        </div>
      </CrossborderFormSection>

      <div className={numericAmount > 0 ? "grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-start" : ""}>
        <CrossborderAmountSection
          title="Valor a enviar da Valut"
          amountInputId="payout-amount"
          amount={amount}
          onAmountChange={setAmount}
          amountCurrencyId={originCurrencyId}
          limits={limits}
          originRailId={sendRailId}
          destinationRailId={beneficiaryRailId}
          fromCurrency={originCurrencyId}
          toCurrency={destinationCurrencyId}
          routePreview={routePreview}
        />

        {numericAmount > 0 && (
          <CrossborderSummary
            title="Prévia da cotação"
            subtitle="Estimativa com câmbio e taxas. Valores sujeitos a confirmação."
            rows={rows}
            footerRows={footerRows}
          />
        )}
      </div>

      {showPageActions && (
        <div className="flex flex-col gap-2 border-t border-gray-100 pt-4">
          <button type="submit" className="btn-primary w-full">
            Solicitar cotação
          </button>
        </div>
      )}
    </form>
  );
};

export const CROSSBORDER_PAYOUT_FORM_ID = PAYOUT_FORM_ID;
